// Enterprise view: pure shaping of the /api/enterprise/* payloads for the
// Companies, Dashboard and Alerts pages.
import { categorizeEvent } from '@trackizy/formatters';

export const KPI_KEYS = ['companies', 'vehicles', 'moving', 'idle', 'stopped', 'offline', 'pending', 'alertsToday', 'alertsUnacknowledged'];

/** Dashboard totals → `[{ key, value }]` in KPI_KEYS order (missing → 0). */
export function kpis(dashboard) {
  return KPI_KEYS.map((key) => ({ key, value: Number(dashboard?.[key]) || 0 }));
}

/**
 * Overview companies → table rows. `resellerOrgs` (GET /api/reseller/overview
 * `orgs`) supplies the device/user limits the Edit dialog needs; a company
 * missing from it is not editable (saving blank limits would clear them).
 */
export function companyRows(overview, resellerOrgs = []) {
  const limits = new Map((resellerOrgs || []).map((o) => [o.id, o]));
  return (overview?.companies || []).map((c) => {
    const org = limits.get(c.id);
    return {
      id: c.id,
      name: c.name,
      vehicles: c.vehicles ?? 0,
      users: c.users ?? 0,
      moving: c.moving ?? 0,
      idle: c.idle ?? 0,
      stopped: c.stopped ?? 0,
      offline: c.offline ?? 0,
      pending: c.pending ?? 0,
      alertsToday: c.alertsToday ?? 0,
      alertsUnacknowledged: c.alertsUnacknowledged ?? 0,
      modules: Array.isArray(c.modules) ? c.modules : [],
      expiresAt: c.expiresAt ?? null,
      effectiveMode: c.effectiveMode || 'none',
      deviceLimit: org?.deviceLimit ?? null,
      userLimit: org?.userLimit ?? null,
      editable: Boolean(org),
    };
  });
}

/** Module slug → readable badge text (`cold-chain` → `Cold chain`). */
export function moduleLabel(slug) {
  const words = String(slug || '').replace(/[-_]+/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : '';
}

/** Rule events (GET /api/enterprise/rules/events `data`) → table rows. */
export function alertRows(events, devices = {}) {
  return (events || []).map((ev) => ({
    id: ev.id,
    time: ev.triggered_at,
    companyId: ev.companyId,
    companyName: ev.companyName,
    vehicle: devices?.[ev.device_id]?.name || `#${ev.device_id}`,
    rule: ev.rule_name || null,
    eventType: ev.event_type,
    category: categorizeEvent(ev.event_type),
    acknowledged: Boolean(ev.acknowledged_at),
  }));
}

const EVENT_FILTERS = ['companyId', 'ruleId', 'shape', 'status', 'category', 'acknowledged'];

/** Filters (+ cursor) → query params for enterprise.events; empty and 'all' are dropped. */
export function eventsParams(filters = {}, cursor = null) {
  const out = {};
  for (const k of EVENT_FILTERS) {
    const v = filters[k];
    if (v != null && v !== '' && v !== 'all') out[k] = String(v);
  }
  if (cursor) out.cursor = cursor;
  return out;
}

/**
 * "Leave enterprise view" is hidden only when the server says there is no
 * home organization (`hasHomeOrg === false`) and no instance role (the
 * platform panel) to land in. A session without the field keeps the button.
 */
export function canLeaveEnterprise(session) {
  return session?.enterprise?.hasHomeOrg !== false || Boolean(session?.instance);
}
